/**
 * DDX-Plus Phase 2 — gap triggers for authoritative source consultation (F.1).
 */

import { FEATURE_STATUS } from './ddxTemporalTaxonomy.js';
import { temporalFitScore } from './ddxKbProjection.js';

/** @typedef {'unresolved_cc'|'sparse_edge_coverage'|'orphan_features'|'thin_cant_miss'|'explicit_clinician_query'} DdxGapType */

/**
 * @param {object} params
 * @param {string | null} params.chiefComplaintId
 * @param {import('./ddxTypes.js').TemporalExtractionResult} params.extraction
 * @param {import('./ddxTypes.js').DdxKnowledgeBundle} params.bundle
 * @param {object[]} params.allEdges - edges for CC (from projection deps), with featureId, diagnosisId, temporalBuckets
 * @param {{ explicitClinicianQuery?: boolean }} [params.flags]
 * @returns {{ gaps: { type: DdxGapType, detail?: string }[], context: object }}
 */
export function detectDdxGaps(params) {
  const { chiefComplaintId, extraction, bundle, allEdges, flags } = params;
  /** @type { { type: DdxGapType, detail?: string }[] } */
  const gaps = [];

  if (!chiefComplaintId) {
    gaps.push({ type: 'unresolved_cc', detail: 'No chief complaint id matched' });
    return { gaps, context: { chiefComplaintId: null } };
  }

  const presentIds = new Set();
  for (const kf of extraction?.keyFeatures || []) {
    if (kf.featureStatus === FEATURE_STATUS.PRESENT && kf.featureId) presentIds.add(kf.featureId);
  }

  const acuity =
    extraction?.acuityEstimate ||
    extraction?.keyFeatures?.[0]?.durationBucket ||
    'indeterminate';

  for (const fid of presentIds) {
    const linked = (allEdges || []).filter((e) => e.featureId === fid && e.direction !== 'contradicts');
    if (linked.length === 0) {
      gaps.push({ type: 'orphan_features', detail: `feature ${fid}` });
    }
  }

  const topDx = (bundle?.candidates || []).slice(0, 5);
  const sparseThreshold = 3;
  for (const c of topDx) {
    const dxEdges = (allEdges || []).filter(
      (e) => e.diagnosisId === c.diagnosisId && presentIds.has(e.featureId) && e.direction !== 'contradicts'
    );
    let inBucket = 0;
    for (const e of dxEdges) {
      const fit = temporalFitScore(String(acuity), e.temporalBuckets);
      if (fit >= 0.7) inBucket += 1;
    }
    if (inBucket > 0 && inBucket < sparseThreshold) {
      gaps.push({
        type: 'sparse_edge_coverage',
        detail: `${c.diagnosisId} has ${inBucket} edges in temporal window (need ${sparseThreshold})`,
      });
      break;
    }
  }

  const cantMiss = bundle?.cantMiss || [];
  if (cantMiss.length > 0 && cantMiss.length < 2) {
    gaps.push({ type: 'thin_cant_miss', detail: `count=${cantMiss.length}` });
  }

  if (flags?.explicitClinicianQuery) {
    gaps.push({ type: 'explicit_clinician_query' });
  }

  return {
    gaps,
    context: {
      chiefComplaintId,
      acuity,
      presentFeatureIds: [...presentIds],
      topDiagnosisIds: topDx.map((x) => x.diagnosisId),
    },
  };
}
